// PRPricing.js - Portugal
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const PRPricing = () => {
  const coverageItems = [
    'Initial eligibility assessment and pathway recommendation',
    'Investment option analysis and guidance',
    'Document preparation and verification',
    'Application form completion and review',
    'Coordination with lawyers and investment advisors',
    'NIF and bank account setup assistance',
    'SEF appointment scheduling and preparation',
    'Post-submission follow-up and support'
  ];

  const importantNotes = [
    'Government application fees are paid directly to Portuguese authorities (SEF)',
    'Investment amounts (€250,000-€500,000) are paid directly to investment vehicles',
    'Legal fees for investment documentation are separate',
    'Property transaction costs (if applicable) are additional',
    'Final residency decisions are made by SEF (Portuguese Immigration Service), not Notapaths. However, 98% of those who follow our guidelines and use our application support succeed. We work closely with immigration requirements to align your application with Portuguese standards, hence our exceptional success rate.'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
              Residency Application Support Pricing
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6 border border-gray-200"
          >
            <h3 className="text-xl font-semibold text-[#202356] mb-4">
              Portuguese Residency Application Support
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              Our comprehensive support service covers all aspects of your Portuguese residency application, from initial assessment to final approval.
            </p>

            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                Our residency application support includes:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-[#202356] mb-4">
                Service Fee
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#202356]/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-[#202356]">Residency Application Support</p>
                    <p className="text-sm text-gray-600">Complete application preparation and submission support</p>
                  </div>
                  <p className="text-2xl font-bold text-[#202356]">$399</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note:</span> This fee covers our professional services for application support. Investment amounts, legal fees, and government fees are separate.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              Final pricing is confirmed after consultation based on your specific pathway and requirements.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-[#202356]">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6"
          >
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              Government Fees (Paid Directly to SEF)
            </h4>
            <div className="space-y-2 text-sm text-gray-700 font-light">
              <p>• <strong>Golden Visa Application:</strong> €533.90 (main applicant)</p>
              <p>• <strong>Golden Visa Renewal:</strong> €2,669.60 (2-year renewal)</p>
              <p>• <strong>D7 Visa Application:</strong> €83 (consulate fee)</p>
              <p>• <strong>Residence Permit Card:</strong> €72.40</p>
              <p>• <strong>Family Members:</strong> Additional fees apply per person</p>
              <p className="text-xs text-gray-600 mt-3">
                Note: Government fees are subject to change. Investment amounts (€250,000-€500,000) are separate and paid directly to investment vehicles.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PRPricing;
